import type { JWT } from "aws-amplify/auth"
import { getUserAttributes } from "../auth-functions/auth-functions"
import type { AuthUser } from "./session.types"

/**
 * Extract user information from Cognito ID token
 * Handles both federated (Google) and non-federated users
 */
export const extractUserFromToken = async (idToken: JWT): Promise<AuthUser> => {
  // Extract base information from ID token
  let email = idToken.payload.email as string | undefined
  let picture = idToken.payload.picture as string | undefined

  // Check if user is federated (e.g., Google OAuth)
  const isFederatedUser = !!idToken.payload.identities

  // For non-federated users, attempt to fetch additional attributes from Cognito
  if (!isFederatedUser) {
    try {
      const attributes = await getUserAttributes()
      if (attributes?.email) {
        email = attributes.email
      }
      if (attributes?.picture) {
        picture = attributes.picture
      }
    } catch (attrError) {
      // Silently fall back to token data if attribute fetch fails
      console.warn("Using token data for user attributes:", attrError)
    }
  }

  return {
    sub: idToken.payload.sub as string,
    email,
    name: idToken.payload.name as string | undefined,
    givenName: idToken.payload.given_name as string | undefined,
    familyName: idToken.payload.family_name as string | undefined,
    picture,
  }
}

/**
 * Check if access token is expired or about to expire
 * @param expirationTime - Token expiration timestamp in seconds
 * @param bufferSeconds - Seconds before expiration to consider token expired (default: 300 = 5 minutes)
 */
export const isTokenExpiringSoon = (
  expirationTime: number,
  bufferSeconds = 300
): boolean => {
  const currentTime = Math.floor(Date.now() / 1000)
  return expirationTime - currentTime <= bufferSeconds
}
